import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatMoney, formatPlacedDate, formatPlacedTime } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import {
  humanPaymentStatus,
  paymentRowId,
  pickMerchantOrderId,
  pickPaymentAmount,
  pickPaymentCustomerName,
  pickPaymentGateway,
  pickPaymentSettledAt,
  PAYMENT_HISTORY_STATUSES,
} from '../lib/payments';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

const EMPTY_FILTERS = {
  status: '',
  gateway: '',
  merchantOrderIdContains: '',
  customerNameContains: '',
  dateFrom: '',
  dateTo: '',
};

function startOfDayIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const t = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00:00.000Z`;
  return t;
}

function endOfDayIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const t = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T23:59:59.999Z`;
  return t;
}

function paymentStatusBadge(status) {
  const s = String(status || '').toUpperCase();
  const map = { SUCCESS: 'success', FAILED: 'danger' };
  const c = map[s] || 'secondary';
  return <span className={`badge text-bg-${c}`}>{humanPaymentStatus(s)}</span>;
}

export default function PaymentHistory() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      if (applied.status) params.status = applied.status;
      if (applied.gateway) params.gateway = applied.gateway;
      if (applied.merchantOrderIdContains.trim()) {
        params.merchantOrderIdContains = applied.merchantOrderIdContains.trim();
      }
      if (applied.customerNameContains.trim()) {
        params.customerNameContains = applied.customerNameContains.trim();
      }
      const from = startOfDayIso(applied.dateFrom);
      const to = endOfDayIso(applied.dateTo);
      if (from) params.createdFrom = from;
      if (to) params.createdTo = to;

      const raw = await adminApi.listPayments(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, applied]);

  useEffect(() => {
    load();
  }, [load]);

  function applyFilters() {
    setApplied({ ...draft });
    setSkip(0);
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setSkip(0);
  }

  function updateDraft(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  const columns = [
    {
      key: 'merchantOrderId',
      label: 'Checkout ref',
      width: '10rem',
      render: (r) => {
        const id = paymentRowId(r);
        const ref = pickMerchantOrderId(r);
        return (
          <>
            <Link to={id ? `/payments/${id}` : '#'} className="fw-semibold text-decoration-none">
              {ref || '—'}
            </Link>
            {id && ref !== id ? (
              <div className="small font-monospace text-secondary text-truncate" title={id}>
                {id}
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'settled',
      label: 'When',
      width: '11rem',
      render: (r) => {
        const iso = pickPaymentSettledAt(r);
        return (
          <div className="small">
            <div className="text-dark text-nowrap">{formatPlacedDate(iso)}</div>
            <div className="text-secondary order-placed-time text-nowrap">{formatPlacedTime(iso)}</div>
          </div>
        );
      },
    },
    {
      key: 'customer',
      label: 'Customer',
      width: '11rem',
      render: (r) => {
        const name = pickPaymentCustomerName(r);
        return name ? (
          <span className="small fw-medium text-dark text-truncate d-block" title={name}>
            {name}
          </span>
        ) : (
          <span className="text-secondary">—</span>
        );
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '7rem',
      render: (r) => <span className="tabular-nums fw-medium">{formatMoney(pickPaymentAmount(r))}</span>,
    },
    {
      key: 'gateway',
      label: 'Gateway',
      width: '7rem',
      render: (r) => {
        const g = pickPaymentGateway(r);
        return g ? <span className="small text-secondary">{g}</span> : '—';
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '8rem',
      render: (r) => paymentStatusBadge(r.status),
    },
    {
      key: 'actions',
      label: '',
      width: '5.5rem',
      render: (r) => {
        const id = paymentRowId(r);
        return id ? (
          <Link to={`/payments/${id}`} className="btn btn-sm btn-outline-primary">
            Details
          </Link>
        ) : (
          '—'
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Payment history" subtitle="Successful and failed checkout attempts" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="payment-filter-status">
                Status
              </label>
              <select
                id="payment-filter-status"
                className="form-select"
                value={draft.status}
                onChange={(e) => updateDraft('status', e.target.value)}
              >
                <option value="">All</option>
                {PAYMENT_HISTORY_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {humanPaymentStatus(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="payment-filter-ref">
                Checkout ref
              </label>
              <input
                id="payment-filter-ref"
                className="form-control"
                value={draft.merchantOrderIdContains}
                onChange={(e) => updateDraft('merchantOrderIdContains', e.target.value)}
                placeholder="ORD-…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="payment-filter-name">
                Customer name
              </label>
              <input
                id="payment-filter-name"
                className="form-control"
                value={draft.customerNameContains}
                onChange={(e) => updateDraft('customerNameContains', e.target.value)}
                placeholder="Name contains…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="payment-filter-gateway">
                Gateway
              </label>
              <select
                id="payment-filter-gateway"
                className="form-select"
                value={draft.gateway}
                onChange={(e) => updateDraft('gateway', e.target.value)}
              >
                <option value="">Any</option>
                <option value="RAZORPAY">Razorpay</option>
                <option value="PHONEPE">PhonePe</option>
              </select>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="payment-filter-from">
                From
              </label>
              <input
                id="payment-filter-from"
                className="form-control"
                type="date"
                value={draft.dateFrom}
                onChange={(e) => updateDraft('dateFrom', e.target.value)}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="payment-filter-to">
                To
              </label>
              <input
                id="payment-filter-to"
                className="form-control"
                type="date"
                value={draft.dateTo}
                onChange={(e) => updateDraft('dateTo', e.target.value)}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 d-flex flex-wrap gap-2">
              <div className="flex-grow-1" style={{ minWidth: '8rem' }}>
                <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                  Apply
                </label>
                <FilterSubmitButton onClick={applyFilters} disabled={loading} />
              </div>
              <div className="flex-grow-1" style={{ minWidth: '8rem' }}>
                <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                  Clear
                </label>
                <button type="button" className="btn btn-outline-secondary w-100" onClick={clearFilters} disabled={loading}>
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      <Card className="admin-orders-table-card">
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : (
            <div className="admin-orders-scroll-boundary">
              <DataTable
                columns={columns}
                rows={rows}
                emptyText="No payments match these filters."
                rowKey={(r, i) => paymentRowId(r) || i}
              />
            </div>
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>
    </>
  );
}
